import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { LazyLoadImageModule } from 'ng-lazyload-image';

import { map, startWith, switchMap, takeUntil, tap, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, firstValueFrom, forkJoin, from, interval, of, combineLatest } from 'rxjs';
import { TransactionReceipt, keccak256 } from 'viem';

import { PunkBillboardComponent } from '@/components/punk-billboard/punk-billboard.component';
import { TxHistoryComponent } from '@/components/tx-history/tx-history.component';

import { WalletAddressDirective } from '@/directives/wallet-address.directive';

import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TraitCountPipe } from '@/pipes/trait-count.pipe';
import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { ThirdPartyBidsPipe } from '@/pipes/third-party-bids.pipe';

import { DataService } from '@/services/data.service';
import { Web3Service } from '@/services/web3.service';
import { StateService } from '@/services/state.service';
import { ThemeService } from '@/services/theme.service';
import { TransactionTrackingService } from '@/services/transaction-tracking.service';
import { AuctionDataService } from '@/services/auction-house/data.service';
import { PlatformService } from '@/services/platform.service';
import { DelegateXYZService } from '@/services/delegate-xyz.service';
import { BidTagsService } from '@/services/bid-tags.service';
import { PunkWrapperService } from '@/services/punk-wrapper.service';
import {
  PunksmarketListingPriceMismatchError,
  PunksmarketV1Service,
} from '@/services/punksmarket-v1.service';

import { Listing, Punk } from '@/models/graph';
import { GlobalState } from '@/models/auction-house/state';
import { initialState } from '@/store/auction-house/data.reducers';
import { environment } from 'src/environments/environment';
import { ZERO_ADDRESS } from '@/constants/utils';
import { LoaderComponent } from '@/components/icons/loader.component';
import { TippyDirective } from '@/directives/tippy.directive';
import { EnsMetadataComponent } from '@/components/ens-metadata/ens-metadata';
import { BidCreationModalComponent, BidType, CopyFromBidPayload } from '@/components/bid-creation-modal/bid-creation-modal.component';
import { StashBidsTableComponent, StashBid } from '@/components/stash-bids-table/stash-bids-table.component';
import { LowerBidConfirmationModalComponent } from '@/components/lower-bid-confirmation-modal/lower-bid-confirmation-modal.component';
import { BidCopyConfirmationModalComponent } from '@/components/bid-copy-confirmation-modal/bid-copy-confirmation-modal.component';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import {
  acceptOnChainBidButtonLabel,
  acceptOnChainBidModalDescription,
  acceptOnChainBidListingRequiredDescription,
  acceptOnChainBidListingRequiredTitle,
  acceptOnChainBidListingPriceHint,
  acceptOnChainBidModalTitle,
  nativeBidMarketplaceSource,
  nativeBidNoun,
  nativeBidNounPlural,
  nativeBidTypeLabel,
  onChainBidAcceptFailure,
  onChainBidNoun,
  placeOnChainBidButtonLabel,
} from '@/utils/native-market-bid-labels';
import {
  mapPunksmarketIndexerBidToUnified,
  unifiedBidToStashTableShape,
} from '@/utils/unified-market-bid.mappers';
import { OnChainBidLabelComponent } from '@/components/on-chain-bid-label/on-chain-bid-label.component';
import { PunksmarketPlaceBidModalComponent } from '@/components/punksmarket-place-bid-modal/punksmarket-place-bid-modal.component';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';
import {
  PUNKSMARKET_V1_ROUTER_ADDRESS,
  punksmarketAppPunkUrl,
} from '@/constants/punksmarket-v1';
import { isOwnedPunkWrapped } from '@/utils/dashboard-sale.util';


@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LoaderComponent,
    ReactiveFormsModule,
    RouterModule,

    LazyLoadImageModule,

    TippyDirective,
    PunkBillboardComponent,
    TxHistoryComponent,
    WalletAddressDirective,
    EnsMetadataComponent,

    TokenIdParsePipe,
    TraitCountPipe,
    WeiToEthPipe,
    FormatCashPipe,
    ThirdPartyBidsPipe,
    BidCreationModalComponent,
    StashBidsTableComponent,
    LowerBidConfirmationModalComponent,
    BidCopyConfirmationModalComponent,
    OnChainBidLabelComponent,
    PunksmarketPlaceBidModalComponent,
  ],
  selector: 'app-punk-item-view',
  templateUrl: './item-view.component.html',
  styleUrls: ['./item-view.component.scss']
})

export class ItemViewComponent implements AfterViewInit, OnDestroy {

  @ViewChild('modal') modal!: ElementRef;

  private punk = new BehaviorSubject<Punk | null>(null);
  punk$ = this.punk.asObservable();
  
  // Add loading state for SSR
  private loading = new BehaviorSubject<boolean>(true);
  loading$ = this.loading.asObservable();

  ZERO_ADDRESS = ZERO_ADDRESS

  auction$ = this.route.params.pipe(
    // Fetch the auction data
    switchMap((params) => {
      if (this.platformService.isServer) {
        // SSR: fetch once only
        return this.getSetAuction(params.tokenId);
      } else {
        // Client: poll every 10 seconds
        return interval(10000).pipe(
          startWith(0),
          switchMap(() => this.getSetAuction(params.tokenId))
        );
      }
    }),
  )

  private destroy$ = new Subject<void>();
  protected usdRate = new BehaviorSubject<number>(0);


  sellModalActive: boolean = false;
  listPrice = new FormControl<number | null>(null);
  listToAddress = new FormControl<string | null>(null);

  bidModalActive: boolean = false;
  bidPrice = new FormControl<number>(0);

  individualBidModalActive: boolean = false;
  individualBidSelectedPunkIds: number[] = [];

  // Copy bid modal state
  copyBidModalActive: boolean = false;
  copyFromBidPayload: CopyFromBidPayload | null = null;
  copyConfirmationModalActive: boolean = false;
  copyConfirmationPunkIds: number[] = [];

  transferModalActive: boolean = false;
  transferAddress = new FormControl<string | null>('');

  acceptBidModalActive: boolean = false;
  acceptPunksmarketBidModalActive: boolean = false;
  acceptPunksmarketListingRequiredModalActive: boolean = false;
  /** Bid selected for accept-on-chain confirmation (main CTA or view-all table). */
  pendingPunksmarketAcceptBid: StashBid | null = null;
  acceptCollectionBidModalActive: boolean = false;
  viewAllCollectionBidsModalActive: boolean = false;
  offerToStashModalActive: boolean = false;
  acceptCollectionBidFinalModalActive: boolean = false;
  stashAddressForListing: string | null = null;
  // transferAddress = new FormControl<string | null>('');

  // Accept collection bid step tracking
  currentAcceptStep: 'initial' | 'offer' | 'accept' | 'approve' = 'initial';

  // Add seller approval state
  sellerApprovalChecked: boolean = false;
  sellerApproved: boolean = true;
  sellerApprovalWarningModalActive: boolean = false;

  // Lower bid confirmation modal state
  lowerBidConfirmationModalActive: boolean = false;
  pendingBidAction: 'legacy' | 'stash' | 'collection' | 'punksmarket' | null = null;
  pendingBidData: any = null;

  // Add delegation rights state
  private hasDelegationRights = new BehaviorSubject<boolean>(false);
  hasDelegationRights$ = this.hasDelegationRights.asObservable();

  // Cross-collection sibling pairing
  siblingPaired = false;
  siblingOwner: string | null = null;

  // Wrap/Unwrap button state
  canShowWrapUnwrapButton = false;
  wrapUnwrapButtonText = '';
  nativeMarketplaceSectionTitle = 'Native marketplace';
  readonly nativeBidNoun = nativeBidNoun;
  readonly nativeBidNounPlural = nativeBidNounPlural;
  readonly nativeBidTypeLabel = nativeBidTypeLabel;
  readonly placeNativeBidButtonLabel = `Place ${nativeBidTypeLabel}`;
  readonly withdrawNativeBidButtonLabel = `Withdraw ${nativeBidTypeLabel}`;
  readonly acceptNativeBidButtonLabel = `Accept ${nativeBidTypeLabel}`;
  readonly cancelNativeBidButtonLabel = `Cancel ${nativeBidTypeLabel}`;
  readonly acceptNativeBidModalTitle = `Accept ${nativeBidNoun} for`;
  readonly onChainBidNoun = onChainBidNoun;
  readonly placeOnChainBidButtonLabel = placeOnChainBidButtonLabel;
  readonly acceptOnChainBidButtonLabel = acceptOnChainBidButtonLabel;
  readonly acceptOnChainBidModalTitle = acceptOnChainBidModalTitle;
  readonly acceptOnChainBidModalDescription = acceptOnChainBidModalDescription;
  readonly acceptOnChainBidListingRequiredTitle = acceptOnChainBidListingRequiredTitle;
  readonly acceptOnChainBidListingRequiredDescription =
    acceptOnChainBidListingRequiredDescription;
  readonly acceptOnChainBidListingPriceHint = acceptOnChainBidListingPriceHint;
  isItemViewTransactionActive = false;

  errorMessage!: any;
  transactionError: boolean = false;
  thirdPartyBids: any[] = [];
  collectionBids: any[] = [];
  highestCollectionBid: any | null = null;
  highestStashBid: any | null = null;

  private _selectedCollectionBid: any | null = null;
  get selectedCollectionBid(): any | null {
    return this._selectedCollectionBid;
  }
  set selectedCollectionBid(value: any | null) {
    this._selectedCollectionBid = value;
  }
  allBids: any[] = []; // Combined stash + punksmarket + legacy bids
  punksmarketTableBids: StashBid[] = [];
  highestPunksmarketBid: StashBid | null = null;
  punksmarketPlaceBidModalActive = false;
  isLegacyBidLower: boolean = false;
  isStashBidLower: boolean = false;
  isPunksmarketBidLower: boolean = false;

  // Precomputed constants for stash CREATE2 derivation
  private readonly STASH_FACTORY_ADDRESS = '0x000000000000A6fA31F5fC51c1640aAc76866750';
  private readonly STASH_INIT_CODE_HASH = '0xe5e58e5bf515959cfde70a49827f414a27af4ee79b57e5fd8cf060d9130fa9b9';
  private readonly STASH_SALT_SUFFIX = BigInt('0x84575f01b760066c746145c8');

  /** Same key as my-bids: "display only bids above min ETH" (see restoreDisplayOnlyBidsAboveMinEthState). */
  private readonly MY_BIDS_STORAGE_KEY_MIN_ETH = 'my-bids-display-only-bids-above-min-eth';
  private readonly minBidEthForDisplay = environment.myBidsMinEth ?? 1;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public dataSvc: DataService,
    private auctionDataSvc: AuctionDataService,
    public web3Svc: Web3Service,
    public stateSvc: StateService,
    public themeSvc: ThemeService,
    public txTrackingSvc: TransactionTrackingService,
    private platformService: PlatformService,
    private delegateXYZService: DelegateXYZService,
    private bidTagsService: BidTagsService,
    private punkWrapperService: PunkWrapperService,
    private punksmarketV1Svc: PunksmarketV1Service,
  ) {
    this.dataSvc.usd$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(rate => this.usdRate.next(rate));

    // Watch for wallet connection to check seller approval and delegation rights
    this.stateSvc.walletAddress$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(address => {
      if (address) {
        const punk = this.punk.getValue();
        if (punk && punk.owner?.id) {
          const currentUserAddress = address.toLowerCase();
          const ownerAddress = punk.owner.id.toLowerCase();
          
          // Check seller approval if there's a listing and it's not the current user's
          if (punk.listing && currentUserAddress !== ownerAddress) {
            this.checkSellerApproval(punk);
          }
          
          // Check delegation rights
          this.checkDelegationRights(address, punk.owner.id);
        }
      }
      
      // Update wrap/unwrap button state when wallet address changes
      this.updateWrapUnwrapButtonState();
    });

    // Update allBids when punk changes (e.g., legacy bid changes)
    this.punk$.pipe(
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.bid?.value === curr?.bid?.value),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateAllBids();
    });

    // Reload collection bids when user connects (to show confidential bids if authenticated)
    combineLatest([
      this.stateSvc.web3Connected$,
      this.stateSvc.walletAddress$,
      this.punk$
    ]).pipe(
      distinctUntilChanged((prev, curr) => 
        prev[0] === curr[0] && prev[1] === curr[1] && prev[2]?.id === curr[2]?.id
      ),
      filter(([isConnected, walletAddress, punk]) => isConnected && !!walletAddress && !!punk?.id),
      switchMap(([isConnected, walletAddress, punk]) => {
        // Reload collection bids when user connects to get confidential bids
        if (!punk?.id) {
          return of([]);
        }
        const stash$ =
          environment.featureFlags?.STASH_BID_SUPPORT !== false
            ? this.dataSvc.fetchCollectionBids(punk.id)
            : of([]);
        const pm$ =
          environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
            ? this.dataSvc.fetchPunksmarketBidsForPunk(punk.id)
            : of([]);
        return forkJoin({ collectionBids: stash$, punksmarketBids: pm$ }).pipe(
          tap(({ collectionBids, punksmarketBids }) => {
            this.applyPunksmarketBidsForTable(punksmarketBids ?? []);
            const mapped = (collectionBids || []).map((bid) => {
              const decodedTags = this.bidTagsService.decodeCollectionBidTags(bid);
              const bidType = this.bidTagsService.getCollectionBidType(bid, decodedTags);
              let stashAddress = this.computeStashAddress(bid?.bidder_address);
              
              // Extract owner address for owner bids
              let ownerAddress: string | undefined;
              if (bidType === 'owner' && decodedTags.length > 0) {
                const ownerTag = decodedTags[0];
                const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
                if (match) {
                  ownerAddress = match[1].toLowerCase();
                }
              }
              
              return {
                ...bid,
                decodedTags,
                bidType,
                stashAddress,
                ownerAddress,
                bidTypeLabel: bidType === 'owner' ? 'Owner Bid' : bidType === 'custom' ? 'Custom Bid' : bidType === 'trait' ? 'Trait Bid' : 'Collection Bid',
                stash_funded: normalizeStashFundedForUi(bid.stash_funded),
              };
            });
            this.collectionBids = this.filterCollectionBidsByMinEthSetting(mapped);

            const fundedBids = this.collectionBids.filter((bid) => bid.stash_funded !== 0);
            if (fundedBids.length > 0) {
              this.highestCollectionBid = [...fundedBids].sort((a, b) =>
                Number(b.bid_amount_eth) - Number(a.bid_amount_eth),
              )[0];
              this.selectedCollectionBid = this.highestCollectionBid;
              const stashBids = fundedBids.filter((bid) => bid.stashAddress);
              this.highestStashBid = stashBids.length > 0
                ? [...stashBids].sort((a, b) => Number(b.bid_amount_eth) - Number(a.bid_amount_eth))[0]
                : null;
            } else {
              this.highestCollectionBid = null;
              this.highestStashBid = null;
              this.selectedCollectionBid = null;
            }

            this.updateAllBids();
          }),
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.route.params.pipe(
      map((params) => `${Number(params.tokenId)}`),
      tap((tokenId) => console.log(`[ItemViewComponent] Starting data fetch for tokenId: ${tokenId}`)),
      // Fetch the punk
      switchMap((tokenId: string) => this.dataSvc.fetchSinglePunk(tokenId).pipe(
        tap((punk: Punk) => this.punk.next(punk)),
        // Fetch the listings from vendors
        switchMap((resPunk: Punk) => {
          console.log(`[ItemViewComponent] Punk fetched, starting additional API calls for punk ${resPunk.id}`);
          return forkJoin({
            punk: of(resPunk),
            allListing: this.dataSvc.fetchAllReservoirListing(resPunk.id),
            cigClaim: this.dataSvc.fetchCigClaim(resPunk.id),
            collectionBids: this.dataSvc.fetchCollectionBids(resPunk.id),
            punksmarketBids:
              environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
                ? this.dataSvc.fetchPunksmarketBidsForPunk(resPunk.id)
                : of([]),
            punksmarketListing:
              environment.collection === 'cryptopunks-v1'
                ? this.dataSvc.getPunksmarketV1ListingForToken(resPunk.id)
                : of(null),
          });
        }),
        tap(({ punk, allListing, cigClaim, collectionBids, punksmarketBids, punksmarketListing }) => {
          if (cigClaim) punk.cigClaim = cigClaim;
          //this.thirdPartyBids = bids ?? [];
          console.log('Third party bids:', this.thirdPartyBids);
          // const resPunk = this.addVendorListing(punk, bestListing)
          // punk.listing = nativeListing;
          const reservoirListings = allListing || [];
          punk.reservoirListings = reservoirListings;

          if (punksmarketListing?.listing) {
            punk.listing = punksmarketListing.listing;
          } else if (punk.listing && DataService.isPunksmarketNativeListing(punk)) {
            punk.listing = { ...punk.listing, nativePunksmarket: true };
          }

          // Keep external listings in reservoirListings only (Other marketplaces section).
          if (
            environment.featureFlags?.USE_ALTERNATIVE_LISTINGS_API &&
            punk.listing?.source &&
            !punk.listing?.nativePunksmarket
          ) {
            punk.listing = null;
          }
          
          // Store collection bids and extract highest bid
          const mappedCollectionBids = (collectionBids || []).map((bid) => {
            const decodedTags = this.bidTagsService.decodeCollectionBidTags(bid);
            const bidType = this.bidTagsService.getCollectionBidType(bid, decodedTags);
            const stashAddress = this.computeStashAddress(bid?.bidder_address);
            
            // Extract owner address for owner bids
            let ownerAddress: string | undefined;
            if (bidType === 'owner' && decodedTags.length > 0) {
              const ownerTag = decodedTags[0];
              const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
              if (match) {
                ownerAddress = match[1].toLowerCase();
              }
            }
            
            return {
              ...bid,
              decodedTags,
              bidType,
              stashAddress,
              ownerAddress,
              bidTypeLabel: bidType === 'owner' ? 'Owner Bid' : bidType === 'custom' ? 'Custom Bid' : bidType === 'trait' ? 'Trait Bid' : 'Collection Bid',
              stash_funded: normalizeStashFundedForUi(bid.stash_funded),
            };
          });
          this.collectionBids = this.filterCollectionBidsByMinEthSetting(mappedCollectionBids);
          this.applyPunksmarketBidsForTable(punksmarketBids ?? []);

          const fundedBids = this.collectionBids.filter((bid) => bid.stash_funded !== 0);
          if (fundedBids.length > 0) {
            this.highestCollectionBid = [...fundedBids].sort((a, b) =>
              Number(b.bid_amount_eth) - Number(a.bid_amount_eth),
            )[0];
            this.selectedCollectionBid = this.highestCollectionBid;
            const stashBids = fundedBids.filter((bid) => bid.stashAddress);
            this.highestStashBid = stashBids.length > 0
              ? [...stashBids].sort((a, b) => Number(b.bid_amount_eth) - Number(a.bid_amount_eth))[0]
              : null;
          } else {
            this.highestCollectionBid = null;
            this.highestStashBid = null;
            this.selectedCollectionBid = null;
          }

          this.punk.next(punk);
          this.loading.next(false);
          
          // Update allBids after punk and collectionBids are set
          this.updateAllBids();
          
          // Update wrap/unwrap button state when punk data changes
          this.updateWrapUnwrapButtonState();
          this.updateNativeMarketplaceSectionTitle();
          
          this.checkSiblingPairing(punk);
          
          // Check seller approval and delegation rights if there's a listing and it's not the current user's
          if (punk.owner?.id && this.stateSvc.getWalletAddress()) {
            const currentUserAddress = this.stateSvc.getWalletAddress()?.toLowerCase();
            const ownerAddress = punk.owner.id.toLowerCase();
            
            if (punk.listing && currentUserAddress !== ownerAddress) {
              this.checkSellerApproval(punk);
            }
            
            // Check delegation rights
            this.checkDelegationRights(this.stateSvc.getWalletAddress()!, punk.owner.id);
          }
        }),
        takeUntil(this.destroy$),
      )),
    ).subscribe();
  }

  getSetAuction(punkId: string): Observable<GlobalState['dataState']['activeAuction']> {
    return from(this.web3Svc.getCurrentBlock()).pipe(
      switchMap((block) => this.auctionDataSvc.fetchAuction(block, punkId)),
    );
  }

  
  isPunksmarketNativeListing(punk: Punk | null | undefined): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      !punk?.wrapped &&
      DataService.isPunksmarketNativeListing(punk)
    );
  }

  isPunksmarketNativeTradingEnabled(): boolean {
    return this.punksmarketV1Svc.isEnabled();
  }

  isUnwrappedV1PunksmarketOwnerFlow(punk: Punk): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      !punk.wrapped &&
      this.isPunksmarketNativeTradingEnabled()
    );
  }

  punksmarketDetailUrl(punk: Punk): string {
    return punksmarketAppPunkUrl(punk.id);
  }

  private async refreshPunkListingAfterTx(
    punkId: string,
    attempts = 3,
  ): Promise<void> {
    for (let i = 0; i < attempts; i++) {
      const current = this.punk.getValue();
      if (!current) {
        return;
      }

      const indexed = await firstValueFrom(
        this.dataSvc.getPunksmarketV1ListingForToken(punkId),
      );
      const punk: Punk = { ...current };
      if (indexed?.listing) {
        punk.listing = { ...indexed.listing, nativePunksmarket: true };
      } else {
        const onChain = await this.punksmarketV1Svc.getDirectedListing(punkId);
        if (onChain) {
          punk.listing = {
            id: punkId,
            value: onChain.minValue.toString(),
            nativePunksmarket: true,
            toAccount: { id: PUNKSMARKET_V1_ROUTER_ADDRESS, punks: [] },
          };
        } else {
          punk.listing = null;
        }
      }
      this.punk.next(punk);
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
  }

  addVendorListing(punk: Punk, listing: Listing): Punk {
    if (listing.wrappedOwner) punk.wrappedOwner = listing.wrappedOwner;
    // return { ...punk, listing: listing.value && (!punk.listing || listing.value < punk.listing?.value) ? listing : punk.listing };
    return punk;
  }

  ngAfterViewInit(): void { 
    // Listener removed - modals are closed manually via buttons
  }

  ngOnInit() {
    // Check for query params to auto-select a bid
    this.route.queryParams.pipe(
      takeUntil(this.destroy$),
      filter(params => params['acceptBid'] === 'true' && params['bidId']),
      switchMap(params => {
        const bidId = params['bidId'];
        return this.punk$.pipe(
          filter(punk => !!punk?.id),
          switchMap(() => {
            if (
              this.collectionBids.length > 0 ||
              this.punksmarketTableBids.length > 0
            ) {
              return of(bidId);
            }
            return interval(100).pipe(
              filter(
                () =>
                  this.collectionBids.length > 0 ||
                  this.punksmarketTableBids.length > 0,
              ),
              take(1),
              map(() => bidId),
            );
          }),
        );
      }),
    ).subscribe((bidId) => {
      setTimeout(() => {
        const stashBid = this.collectionBids.find(
          (b) => b.id === bidId || b.id === String(bidId),
        );
        if (stashBid) {
          this.selectCollectionBid(stashBid);
          return;
        }

        const bidIdStr = String(bidId);
        const pmBid = this.punksmarketTableBids.find(
          (b) =>
            b.id === bidIdStr ||
            b.punksmarketBidId === bidIdStr ||
            (bidIdStr.startsWith('pm-') &&
              b.punksmarketBidId === bidIdStr.slice(3)),
        );
        if (pmBid) {
          this.proceedWithBidAcceptance(pmBid);
        }
      }, 100);
    });

    window.addEventListener('ownedWrapStatusChanged', this.handleOwnedWrapStatusChanged);
  }

  ngOnDestroy(): void {
    window.removeEventListener('ownedWrapStatusChanged', this.handleOwnedWrapStatusChanged);
    this.destroy$.next();
    this.destroy$.complete();
  }


  closeAcceptBid(): void {
    this.acceptBidModalActive = false;
  }

  closeAcceptCollectionBid(): void {
    this.acceptCollectionBidModalActive = false;
    // Reset to highest bid when closing
    this.selectedCollectionBid = this.highestCollectionBid;
    // Reset step tracking
    this.currentAcceptStep = 'initial';
  }

  selectCollectionBid(bid: any): void {
    // Unfunded stash bids do not count as top
    const highestBid = this.allBids.find((b) => b.stash_funded !== 0) ?? null;
    
    if (highestBid) {
      const bidAmount = Number(bid.bid_amount_eth || 0);
      const highestAmount = Number(highestBid.bid_amount_eth || 0);
      const isNotHighest = bid.id !== highestBid.id && bidAmount < highestAmount;
      
      if (isNotHighest) {
        // Close any other modals first
        this.acceptBidModalActive = false;
        this.acceptPunksmarketBidModalActive = false;
        this.pendingPunksmarketAcceptBid = null;
        this.acceptCollectionBidModalActive = false;
        this.viewAllCollectionBidsModalActive = false;

        // Show confirmation modal
        this.pendingBidAction =
          bid.isLegacyBid || bid.bidType === 'legacy'
            ? 'legacy'
            : bid.platform === 'punksmarket' || bid.punksmarketBidId
              ? 'punksmarket'
              : bid.stashAddress
                ? 'stash'
                : 'collection';
        this.pendingBidData = bid;
        this.lowerBidConfirmationModalActive = true;
        return;
      }
    }

    // If it's the highest bid or no comparison possible, proceed normally
    this.proceedWithBidAcceptance(bid);
  }

  proceedWithBidAcceptance(bid: any): void {
    if (bid.isLegacyBid || bid.bidType === 'legacy') {
      this.closeViewAllCollectionBids();
      this.acceptBidForPunk(this.punk.value!);
    } else if (bid.platform === 'punksmarket' || bid.punksmarketBidId) {
      this.closeViewAllCollectionBids();
      this.openAcceptPunksmarketBidModal(bid);
    } else {
      this.selectedCollectionBid = bid;
      this.openAcceptCollectionBidModal();
    }
  }

  confirmLowerBidAcceptance(): void {
    if (!this.pendingBidAction || !this.pendingBidData) {
      console.error('Missing pending bid action or data');
      return;
    }

    const bid = this.pendingBidData;
    const action = this.pendingBidAction;
    
    // Close confirmation modal first
    this.lowerBidConfirmationModalActive = false;
    
    // Use setTimeout to ensure modal closes before opening the next one
    setTimeout(() => {
      // Clear pending data
      this.pendingBidAction = null;
      this.pendingBidData = null;
      
      if (action === 'legacy') {
        // For legacy bid, open the accept bid modal
        this.acceptBidModalActive = true;
      } else if (action === 'stash') {
        // For stash bid, proceed with collection bid modal
        this.selectedCollectionBid = bid;
        this.openAcceptCollectionBidModal();
      } else if (action === 'collection') {
        this.proceedWithBidAcceptance(bid);
      } else if (action === 'punksmarket') {
        this.openAcceptPunksmarketBidModal(bid);
      }
    }, 100);
  }

  cancelLowerBidAcceptance(): void {
    this.lowerBidConfirmationModalActive = false;
    this.pendingBidAction = null;
    this.pendingBidData = null;
  }

  handleAcceptLegacyBidClick(): void {
    if (this.isLegacyBidLower) {
      // Close any other modals first
      this.acceptBidModalActive = false;
      this.acceptPunksmarketBidModalActive = false;
      this.pendingPunksmarketAcceptBid = null;
      this.acceptCollectionBidModalActive = false;
      this.viewAllCollectionBidsModalActive = false;

      // Show confirmation modal
      this.pendingBidAction = 'legacy';
      this.pendingBidData = this.punk.value;
      this.lowerBidConfirmationModalActive = true;
    } else {
      // Proceed normally
      this.acceptBidModalActive = true;
    }
  }

  handleAcceptStashBidClick(): void {
    if (this.isStashBidLower) {
      // Close any other modals first
      this.acceptBidModalActive = false;
      this.acceptPunksmarketBidModalActive = false;
      this.pendingPunksmarketAcceptBid = null;
      this.acceptCollectionBidModalActive = false;
      this.viewAllCollectionBidsModalActive = false;

      // Show confirmation modal
      this.pendingBidAction = 'stash';
      this.pendingBidData = this.highestCollectionBid;
      this.lowerBidConfirmationModalActive = true;
    } else {
      // Proceed normally
      this.selectedCollectionBid = this.highestCollectionBid;
      this.openAcceptCollectionBidModal();
    }
  }

  canAcceptCollectionBid = (bid: any): boolean => {
    const isOwner =
      this.punk.value?.owner?.id?.toLowerCase() ===
      this.stateSvc.getWalletAddress()?.toLowerCase();
    if (!isOwner) {
      return false;
    }
    if (bid.platform === 'punksmarket' || bid.punksmarketBidId) {
      return this.isPunksmarketBidNativeEnabled() && !!this.punk.value;
    }
    return true;
  };

  /**
   * Same semantics as my-bids restoreDisplayOnlyBidsAboveMinEthState / dashboard displayOnlyBidsAboveMinEthFromSettings.
   */
  private displayOnlyBidsAboveMinEthFromSettings(): boolean {
    if (!this.platformService.isClient) {
      return true;
    }
    const raw = this.platformService.getLocalStorageItem(this.MY_BIDS_STORAGE_KEY_MIN_ETH);
    return raw !== 'false';
  }

  private filterCollectionBidsByMinEthSetting(bids: any[]): any[] {
    if (!this.displayOnlyBidsAboveMinEthFromSettings()) {
      return bids;
    }
    return bids.filter((b) => {
      const amount = Number(b.bid_amount_eth) || 0;
      if (amount >= this.minBidEthForDisplay) {
        return true;
      }
      const targetedCount =
        b.punk_indices_count ??
        b.punkIndices?.length ??
        (Array.isArray(b.selected_punk_ids) ? b.selected_punk_ids.length : 0);
      return (
        b.bidType === 'custom' ||
        b.bidType === 'owner' ||
        targetedCount > 0
      );
    });
  }

  /**
   * Update allBids property (collection bids + legacy bid if exists)
   * This should be called whenever collectionBids or punk changes
   */
  isPunksmarketBidSupportEnabled(): boolean {
    return environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false;
  }

  isPunksmarketBidNativeEnabled(): boolean {
    return environment.featureFlags?.PUNKSMARKET_BID_NATIVE !== false;
  }

  private applyPunksmarketBidsForTable(items: PunksmarketIndexerBid[]): void {
    const mapped = items
      .filter((b) => b.active !== false)
      .map((item) => {
        const unified = mapPunksmarketIndexerBidToUnified(item);
        return unifiedBidToStashTableShape(unified) as StashBid;
      });
    // On-chain PunksMarket bids are already scoped to this punk; do not apply the
    // global "my bids min ETH" display filter (often 1 ETH) used for stash/collection grids.
    this.punksmarketTableBids = mapped as StashBid[];
    this.highestPunksmarketBid =
      this.punksmarketTableBids.length > 0
        ? [...this.punksmarketTableBids].sort(
            (a, b) => Number(b.bid_amount_eth) - Number(a.bid_amount_eth),
          )[0]
        : null;
  }

  private updateAllBids(): void {
    const allBids = [...this.collectionBids, ...this.punksmarketTableBids];
    const punk = this.punk.value;
    
    // Add legacy bid if it exists (respect same min-ETH display setting as my-bids / stash table modal)
    if (punk?.bid?.value) {
      const legacyEth = parseFloat(this.web3Svc.weiToEth(punk.bid.value));
      const includeLegacy =
        !this.displayOnlyBidsAboveMinEthFromSettings() ||
        legacyEth >= this.minBidEthForDisplay;
      if (includeLegacy) {
        const legacyBid = {
          id: `legacy-${punk.id}`,
          bidder_address: punk.bid.fromAccount.id,
          bid_amount_wei: punk.bid.value,
          bid_amount_eth: legacyEth,
          bidType: 'legacy' as const,
          bidTypeLabel: nativeBidTypeLabel,
          source: nativeBidMarketplaceSource,
          isLegacyBid: true,
          stash_funded: 1 as const,
          expires_at: null,
          created_at: punk.bid.blockTimestamp,
          // Legacy bid specific fields
          legacyBid: punk.bid
        };
        allBids.push(legacyBid);
      }
    }
    
    // Sort by bid amount descending
    this.allBids = allBids.sort((a, b) => Number(b.bid_amount_eth) - Number(a.bid_amount_eth));
    
    // Update bid comparison flags
    this.updateBidComparison();
  }

  /**
   * Update bid comparison flags to determine which bid should be disabled
   * This should be called whenever punk or collectionBids change
   */
  private updateBidComparison(): void {
    const punk = this.punk.value;
    const top = this.allBids.find((b) => b.stash_funded !== 0) ?? null;
    const topEth = top ? Number(top.bid_amount_eth) : 0;

    const legacyEth = punk?.bid?.value
      ? parseFloat(this.web3Svc.weiToEth(punk.bid.value))
      : null;
    const stashEth = this.highestStashBid
      ? Number(this.highestStashBid.bid_amount_eth)
      : null;
    const pmEth = this.highestPunksmarketBid
      ? Number(this.highestPunksmarketBid.bid_amount_eth)
      : null;

    this.isLegacyBidLower =
      legacyEth != null && (topEth === 0 || legacyEth < topEth);
    this.isStashBidLower =
      stashEth != null && topEth > 0 && stashEth < topEth;
    this.isPunksmarketBidLower =
      pmEth != null && topEth > 0 && pmEth < topEth;
  }

  handleAcceptPunksmarketBidClick(): void {
    if (!this.highestPunksmarketBid) {
      return;
    }
    if (this.isPunksmarketBidLower) {
      this.pendingBidAction = 'punksmarket';
      this.pendingBidData = this.highestPunksmarketBid;
      this.lowerBidConfirmationModalActive = true;
      return;
    }
    this.openAcceptPunksmarketBidModal(this.highestPunksmarketBid);
  }

  openAcceptPunksmarketBidModal(bid: StashBid): void {
    if (!bid?.punksmarketBidId) {
      return;
    }
    if (!this.isPunksmarketBidNativeEnabled()) {
      window.open('https://punksmarket.app', '_blank', 'noopener,noreferrer');
      return;
    }
    if (this.stateSvc.isMimicking()) {
      this.errorMessage =
        'Deactivate mimic mode before signing an on-chain transaction.';
      return;
    }
    this.pendingPunksmarketAcceptBid = bid;
    const punk = this.punk.value;
    if (punk && !this.isPunksmarketNativeListing(punk)) {
      this.acceptPunksmarketListingRequiredModalActive = true;
      return;
    }
    this.acceptPunksmarketBidModalActive = true;
  }

  closeAcceptPunksmarketListingRequiredModal(): void {
    this.acceptPunksmarketListingRequiredModalActive = false;
    this.pendingPunksmarketAcceptBid = null;
  }

  /** Standard list-for-sale (manual price). */
  openSellModal(): void {
    this.pendingPunksmarketAcceptBid = null;
    this.listPrice.setValue(null);
    this.sellModalActive = true;
  }

  /** Directed listing before acceptBid — price prefilled from the pending on-chain bid. */
  openSellModalForPunksmarketAccept(): void {
    this.acceptPunksmarketListingRequiredModalActive = false;
    const bid = this.pendingPunksmarketAcceptBid;
    const listingEth = bid?.bid_amount_eth != null ? Number(bid.bid_amount_eth) : null;
    if (listingEth != null && listingEth > 0) {
      this.listPrice.setValue(listingEth);
    }
    this.sellModalActive = true;
  }

  closeAcceptPunksmarketBidModal(): void {
    this.acceptPunksmarketBidModalActive = false;
    this.pendingPunksmarketAcceptBid = null;
  }

  async confirmAcceptPunksmarketBid(): Promise<void> {
    const bid = this.pendingPunksmarketAcceptBid;
    if (!bid) {
      return;
    }
    this.closeAcceptPunksmarketBidModal();
    await this.acceptPunksmarketBid(bid);
  }

  async acceptPunksmarketBid(bid: StashBid): Promise<void> {
    const punk = this.punk.value;
    if (!punk?.id || !bid.punksmarketBidId) {
      return;
    }
    if (!this.isPunksmarketBidNativeEnabled()) {
      window.open('https://punksmarket.app', '_blank', 'noopener,noreferrer');
      return;
    }
    if (this.stateSvc.isMimicking()) {
      this.errorMessage =
        'Deactivate mimic mode before signing an on-chain transaction.';
      return;
    }

    try {
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.punksmarketV1Svc.acceptBid(
        bid.punksmarketBidId,
        punk.id,
      );
      if (!hash) {
        throw new Error(onChainBidAcceptFailure);
      }

      this.txTrackingSvc.setTransactionMessage(hash);
      const receipt = await this.pollReceipt(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);

      await this.refreshPunkListingAfterTx(punk.id);
      this.dataSvc.fetchPunksmarketBidsForPunk(punk.id).subscribe((items) => {
        this.applyPunksmarketBidsForTable(items);
        this.updateAllBids();
      });
      this.dataSvc.fetchSinglePunk(punk.id).subscribe((updated) => {
        if (updated) {
          this.punk.next(updated);
        }
      });

      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err: unknown) {
      console.error('acceptPunksmarketBid failed', err);
      if (err instanceof PunksmarketListingPriceMismatchError) {
        this.txTrackingSvc.setErrorTransactionMessage(
          new Error(
            'The listing price changed on-chain. Refresh the page and try again.',
          ),
        );
        await this.refreshPunkListingAfterTx(punk.id, 1);
      } else {
        this.txTrackingSvc.setErrorTransactionMessage(
          err instanceof Error ? err : new Error(onChainBidAcceptFailure),
        );
        this.errorMessage =
          err instanceof Error ? err.message : onChainBidAcceptFailure;
      }
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  openPunksmarketPlaceBidModal(): void {
    this.punksmarketPlaceBidModalActive = true;
  }

  closePunksmarketPlaceBidModal(): void {
    this.punksmarketPlaceBidModalActive = false;
  }

  onPunksmarketBidPlaced(): void {
    const punkId = this.punk.value?.id;
    if (!punkId) {
      return;
    }
    this.dataSvc.fetchPunksmarketBidsForPunk(punkId).subscribe((items) => {
      this.applyPunksmarketBidsForTable(items);
      this.updateAllBids();
    });
  }

  openAcceptCollectionBidModal(): void {
    this.currentAcceptStep = 'initial';
    this.acceptCollectionBidModalActive = true;
    this.viewAllCollectionBidsModalActive = false;
  }

  viewAllCollectionBids(): void {
    this.viewAllCollectionBidsModalActive = true;
  }

  closeViewAllCollectionBids(): void {
    this.viewAllCollectionBidsModalActive = false;
  }

  private normalizeHexAddress(address: string | null | undefined): string | null {
    if (!address) {
      return null;
    }
    const trimmed = address.trim();
    if (!trimmed) {
      return null;
    }
    const prefixed = trimmed.startsWith('0x') ? trimmed : `0x${trimmed}`;
    return /^0x[0-9a-fA-F]{40}$/.test(prefixed) ? prefixed : null;
  }

  private computeStashSalt(ownerAddress: string | null | undefined): `0x${string}` | null {
    const normalized = this.normalizeHexAddress(ownerAddress);
    if (!normalized) {
      return null;
    }
    try {
      const ownerValue = BigInt(normalized);
      const salt = (ownerValue << 96n) | this.STASH_SALT_SUFFIX;
      return `0x${salt.toString(16).padStart(64, '0')}` as `0x${string}`;
    } catch {
      return null;
    }
  }

  private computeStashAddress(ownerAddress: string | null | undefined): string | null {
    const salt = this.computeStashSalt(ownerAddress);
    if (!salt) {
      return null;
    }
    const payload = `0xff${this.STASH_FACTORY_ADDRESS.slice(2)}${salt.slice(2)}${this.STASH_INIT_CODE_HASH.slice(2)}` as `0x${string}`;
    const hash = keccak256(payload);
    return `0x${hash.slice(-40)}`;
  }

  closeListing(): void {
    this.sellModalActive = false;
    // this.listPrice.setValue(this.dataSvc.getFloor());
  }

  closeTransfer(): void {
    this.transferModalActive = false;
    this.transferAddress.setValue('');
  }

  closeBid(): void {
    this.bidModalActive = false;
    this.bidPrice.setValue(0);
  }

  openIndividualBidModal(): void {
    const punk = this.punk.getValue();
    if (punk && punk.id) {
      this.individualBidSelectedPunkIds = [Number(punk.id)];
      this.individualBidModalActive = true;
    }
  }

  closeIndividualBidModal(): void {
    this.individualBidModalActive = false;
    this.individualBidSelectedPunkIds = [];
  }

  onIndividualBidCreated(): void {
    // Just close the modal - the existing combineLatest subscription will handle data reload
    this.closeIndividualBidModal();
  }

  private static readonly COPY_BID_AMOUNT_INCREMENT_ETH = 0.01;
  private static readonly EXPIRY_DAY_OPTIONS = [1, 3, 7, 30, 365];

  openCopyBidModal(bid: StashBid): void {
    this.dataSvc.fetchBidDetailsWithPunkIndices(bid.id).subscribe({
      next: (bidDetails: any) => {
        this.buildCopyBidPayload(bid, bidDetails);
      },
      error: () => {
        this.buildCopyBidPayload(bid, null);
      }
    });
  }

  private buildCopyBidPayload(bid: StashBid, bidDetails: any): void {
    const modalBidType: BidType = bid.bidType === 'custom' ? 'targeted'
      : (bid.bidType === 'owner' || bid.bidType === 'trait' || bid.bidType === 'collection')
        ? bid.bidType
        : 'collection';

    let punkIndices: number[] = [];
    let selectedPunkIds: number[] = [];

    const detailPunkIds = this.extractPunkIdsFromDetails(bidDetails);

    if (modalBidType === 'owner' || modalBidType === 'trait') {
      punkIndices = detailPunkIds.length > 0 ? detailPunkIds : (bid.punkIds ?? bid.punkIndices ?? []);
    } else if (modalBidType === 'targeted') {
      if (detailPunkIds.length > 0) {
        selectedPunkIds = detailPunkIds;
      } else {
        const rawIds = bid.selected_punk_ids ?? bid.punkIds ?? bid.punkIndices ?? [];
        selectedPunkIds = Array.isArray(rawIds)
          ? rawIds
          : typeof rawIds === 'string'
            ? rawIds.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
            : [];
      }
    }

    let expiryDays: number | undefined;
    if (bid.expires_at && bid.created_at) {
      const diffMs = new Date(bid.expires_at).getTime() - new Date(bid.created_at).getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        expiryDays = ItemViewComponent.EXPIRY_DAY_OPTIONS.reduce((prev, curr) =>
          Math.abs(curr - diffDays) < Math.abs(prev - diffDays) ? curr : prev
        );
      }
    }

    this.copyFromBidPayload = {
      amountEth: bid.bid_amount_eth + ItemViewComponent.COPY_BID_AMOUNT_INCREMENT_ETH,
      expiryDays,
      confidential: bid.confidential ?? false,
      bidType: modalBidType,
      ownerAddress: bid.ownerAddress ?? null,
      punkIndices,
      selectedPunkIds,
      activeTraits: bid.decodedTags ?? [],
      filterTag: bid.tag ?? ''
    };

    const allPunkIds = [...punkIndices, ...selectedPunkIds];
    if (modalBidType !== 'collection' && allPunkIds.length > 0) {
      this.copyConfirmationPunkIds = allPunkIds;
      this.copyConfirmationModalActive = true;
    } else {
      this.copyBidModalActive = true;
    }
  }

  private extractPunkIdsFromDetails(bidDetails: any): number[] {
    if (!bidDetails?.parsed_punk_indices) return [];
    if (bidDetails.parsed_punk_indices === 'all') return [];
    if (Array.isArray(bidDetails.parsed_punk_indices)) return bidDetails.parsed_punk_indices;
    return [];
  }

  onCopyConfirmed(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyBidModalActive = true;
  }

  onCopyCancelled(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyFromBidPayload = null;
  }

  closeCopyBidModal(): void {
    this.copyBidModalActive = false;
    this.copyFromBidPayload = null;
  }

  onCopyBidCreated(): void {
    this.closeCopyBidModal();
  }

  closeAll(): void {
    this.txTrackingSvc.closeTxModal();
    this.sellModalActive = false;
    this.bidModalActive = false;
    this.transferModalActive = false;
    this.acceptBidModalActive = false;
    this.acceptPunksmarketBidModalActive = false;
    this.acceptPunksmarketListingRequiredModalActive = false;
    this.pendingPunksmarketAcceptBid = null;
    this.acceptCollectionBidModalActive = false;
    this.viewAllCollectionBidsModalActive = false;
    this.offerToStashModalActive = false;
    this.acceptCollectionBidFinalModalActive = false;
    this.sellerApprovalWarningModalActive = false;
    this.individualBidModalActive = false;
    this.copyBidModalActive = false;
    this.copyConfirmationModalActive = false;
    this.lowerBidConfirmationModalActive = false;
    this.pendingBidAction = null;
    this.pendingBidData = null;
    // Reset accept step tracking
    this.currentAcceptStep = 'initial';
  }

  async submitListing(punk: Punk): Promise<void> {
    if (this.isUnwrappedV1PunksmarketOwnerFlow(punk)) {
      return this.submitPunksmarketListing(punk);
    }

    try {
      const tokenId = punk.id;

      const value = this.listPrice.value;
      if (!value) return;

      let toAddress: string | null = this.listToAddress.value;
      toAddress = await this.web3Svc.verifyAddressOrEns(toAddress);

      this.closeListing();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const submittedAtBlock = await this.web3Svc.getCurrentBlockNumber();
      const result = await this.web3Svc.ensureApprovalAndList(tokenId, value, toAddress);
      
      if (result.approvalHash) {
        // Approval is needed
        this.txTrackingSvc.setTransactionMessage(result.approvalHash, 'Approving collection for listing...');
        const approvalReceipt = await this.pollReceipt(result.approvalHash);
        this.txTrackingSvc.setProcessTransactionMessage(approvalReceipt, 'Collection approval confirmed. Now listing...');
        
        // Now proceed with listing
        const listingHash = await this.web3Svc.offerPunkForSale(tokenId, value, toAddress);
        this.txTrackingSvc.setTransactionMessage(listingHash!);
        
        const listingReceipt = await this.pollReceipt(listingHash!);
        this.txTrackingSvc.setProcessTransactionMessage(listingReceipt);
        this.registerV1ListingUiOrigin(tokenId, listingHash!, listingReceipt, value, submittedAtBlock);
        setTimeout(() => {
          this.isItemViewTransactionActive = false;
        }, 3000);
      } else if (result.listingHash) {
        // No approval needed, direct listing
        this.txTrackingSvc.setTransactionMessage(result.listingHash);
        
        const receipt = await this.pollReceipt(result.listingHash);
        this.txTrackingSvc.setProcessTransactionMessage(receipt);
        this.registerV1ListingUiOrigin(tokenId, result.listingHash, receipt, value, submittedAtBlock);
        setTimeout(() => {
          this.isItemViewTransactionActive = false;
        }, 3000);
      }
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async submitPunksmarketListing(punk: Punk): Promise<void> {
    try {
      const tokenId = punk.id;
      const value = this.listPrice.value;
      if (!value || value <= 0) {
        return;
      }

      this.closeListing();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const submittedAtBlock = await this.web3Svc.getCurrentBlockNumber();
      const hash = await this.punksmarketV1Svc.listUnwrappedPunk(tokenId, value);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      this.registerV1ListingUiOrigin(
        tokenId,
        hash!,
        receipt,
        value,
        submittedAtBlock,
        'v1-punksmarket-listing',
      );
      await this.refreshPunkListingAfterTx(tokenId);

      const pendingAccept = this.pendingPunksmarketAcceptBid;
      if (
        pendingAccept &&
        this.punk.value &&
        this.isPunksmarketNativeListing(this.punk.value)
      ) {
        this.openAcceptPunksmarketBidModal(pendingAccept);
      }

      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async buyPunkViaPunksmarket(punk: Punk): Promise<void> {
    try {
      if (this.hasDelegationRights.getValue()) {
        return;
      }

      const tokenId = punk.id;
      const value = punk.listing?.value;
      if (!value) {
        return;
      }

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.punksmarketV1Svc.buyListedPunk(tokenId, value);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      await this.refreshPunkListingAfterTx(tokenId);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      if (err instanceof PunksmarketListingPriceMismatchError) {
        this.txTrackingSvc.setErrorTransactionMessage(
          new Error(
            'The listing price changed on-chain. Refresh the page and try again.',
          ),
        );
        await this.refreshPunkListingAfterTx(punk.id, 1);
      } else {
        this.txTrackingSvc.setErrorTransactionMessage(err);
      }
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async punkNoLongerForSale(punk: Punk): Promise<void> {
    try {
      const tokenId = punk.id;

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash =
        this.isPunksmarketNativeListing(punk) &&
        this.isPunksmarketNativeTradingEnabled()
          ? await this.punksmarketV1Svc.delistUnwrappedPunk(tokenId)
          : await this.web3Svc.punkNoLongerForSale(tokenId);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      // const receipt = await this.web3Svc.waitForTransaction(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      if (
        this.isPunksmarketNativeListing(punk) &&
        this.isPunksmarketNativeTradingEnabled()
      ) {
        await this.refreshPunkListingAfterTx(tokenId);
      }
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async withdrawBidForPunk(punk: Punk): Promise<void> {
    try {
      const tokenId = punk.id;

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.withdrawBidForPunk(tokenId);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      // const receipt = await this.web3Svc.waitForTransaction(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async cancelBidWithFlashLoan(punk: Punk): Promise<void> {
    try {
      if (!punk.bid?.value) {
        throw new Error('No bid found for this punk');
      }

      const tokenId = punk.id;
      const bidValue = punk.bid.value;

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.cancelBidWithFlashLoan(tokenId, bidValue);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async acceptBidForPunk(punk: Punk): Promise<void> {
    try {
      const tokenId = punk.id;
      const value = punk.bid?.value;

      this.closeAcceptBid();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.acceptBidForPunk(tokenId, value!);
      const shortHash = `${hash?.slice(0, 6)}...${hash?.slice(-6)}`;

      this.txTrackingSvc.setTransactionMessage(
        hash!,
        `<p>Your transaction was sent to the MEV Blocker builder and is waiting to be processed.</p>
        <p>Transaction hash: <a href="https://etherscan.io/tx/${hash}" target="_blank">${shortHash}</a></p>
        <p>You can <a href="https://etherscan.io/tx/${hash}" target="_blank">view it here</a> once complete.</p>
      `);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async acceptCollectionBidForPunk(punk: Punk): Promise<void> {
    try {
      if (!this.selectedCollectionBid) {
        throw new Error('No collection bid found for this punk');
      }

      const tokenId = punk.id;
      const collectionBid = this.selectedCollectionBid;

      // Step 1: Get the stash address for the bidder
      const stashAddress = await this.web3Svc.getStashAddressFor(collectionBid.bidder_address);

      // V2: UI flags; V1 WPV1: use chain exists() — punk.wrapped can be wrong for native-only punks
      const isWrapped = punk.wrapped || punk.c721wrapped;

      if (environment.collection === 'cryptopunks-v1') {
        // V1 collection — only ERC721 path needs stash approval
        const wpv1Minted = await this.web3Svc.wrappedPunkV1TokenExists(tokenId);
        if (wpv1Minted) {
          // WPV1 wrapped: check stash approval
          const isApproved = await this.web3Svc.checkStashApproval(tokenId, stashAddress);

          if (!isApproved) {
            // Show approval modal for V1 wrapped
            this.stashAddressForListing = stashAddress;
            this.currentAcceptStep = 'approve';
            this.acceptCollectionBidModalActive = false;
            this.sellerApprovalWarningModalActive = true;
            return;
          }

          // If approved, proceed directly to accept the bid
          this.currentAcceptStep = 'accept';
          this.closeAcceptCollectionBid();
          this.acceptCollectionBidFinalModalActive = true;
        } else {
          // V1 non-wrapped: check if offered to stash at 0 (native transfer to bidder, ETH paid from stash)
          const isOfferedToStash = await this.web3Svc.checkPunkOfferedToStash(
            tokenId,
            stashAddress,
            '0', // V1 listing must be 0 wei; bid is settled in ETH by the stash
            environment.v1PunksMarketAddress
          );

          if (!isOfferedToStash) {
            // Show modal to offer the punk to the stash
            this.stashAddressForListing = stashAddress;
            this.currentAcceptStep = 'offer';
            this.acceptCollectionBidModalActive = false;
            this.offerToStashModalActive = true;
            return;
          }

          // If offered to stash, proceed to accept the bid (stash buys at 0, delivers native punk to bidder)
          this.currentAcceptStep = 'accept';
          // this.closeAcceptCollectionBid();
          this.acceptCollectionBidModalActive = false;
          this.acceptCollectionBidFinalModalActive = true;
        }
      } else {
        // V2 (or other) collection
        if (isWrapped) {
          // V2 wrapped: check stash approval (C721 or legacy wrapper)
          const isApproved = await this.web3Svc.checkStashApproval(tokenId, stashAddress, punk.c721wrapped);

          if (!isApproved) {
            // Show approval modal for V2 wrapped
            this.stashAddressForListing = stashAddress;
            this.currentAcceptStep = 'approve';
            this.acceptCollectionBidModalActive = false;
            this.sellerApprovalWarningModalActive = true;
            return;
          }

          // If approved, proceed directly to accept the bid
          this.currentAcceptStep = 'accept';
          this.closeAcceptCollectionBid();
          this.acceptCollectionBidFinalModalActive = true;
        } else {
          // V2 non-wrapped: use existing offerTo flow
          const isOfferedToStash = await this.web3Svc.checkPunkOfferedToStash(
            tokenId,
            stashAddress,
            collectionBid.bid_amount_wei
          );

          if (!isOfferedToStash) {
            // Show modal to offer the punk to the stash
            this.stashAddressForListing = stashAddress;
            this.currentAcceptStep = 'offer';
            this.acceptCollectionBidModalActive = false;
            this.offerToStashModalActive = true;
            return;
          }

          // Step 3: Show final modal to accept the collection bid
          this.currentAcceptStep = 'accept';
          // this.closeAcceptCollectionBid();
          this.acceptCollectionBidModalActive = false;
          this.acceptCollectionBidFinalModalActive = true;
        }
      }
    } catch (err: any) {
      console.log(err);
      
      // Handle stash approval requirement for V1
      if (err.code === 'STASH_APPROVAL_REQUIRED' || err.message === 'STASH_APPROVAL_REQUIRED') {
        const stashAddress = await this.web3Svc.getStashAddressFor(
          this.selectedCollectionBid?.bidder_address || ''
        );
        this.stashAddressForListing = stashAddress;
        this.currentAcceptStep = 'approve';
        this.acceptCollectionBidModalActive = false;
        this.sellerApprovalWarningModalActive = true;
        return;
      }
      
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async offerPunkToStash(punk: Punk): Promise<void> {
    try {
      if (!this.stashAddressForListing) {
        throw new Error('No stash address specified');
      }

      const stashAddress = this.stashAddressForListing;
      const tokenId = punk.id;
      const isWrapped = punk.wrapped || punk.c721wrapped;

      this.currentAcceptStep = 'accept';
      this.closeOfferToStashModal();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      // V1 non-wrapped: offer to stash at 0 on the V1 market so the stash can buy and settle in ETH
      if (environment.collection === 'cryptopunks-v1' && !isWrapped) {
        // List at 0 wei to the stash address on the CryptoPunks V1 contract
        this.txTrackingSvc.setTransactionMessage('', 'Offering punk to stash...');
        const offerHash = await this.web3Svc.offerPunkForSale(tokenId, 0, stashAddress, environment.v1PunksMarketAddress);
        
        if (offerHash) {
          this.txTrackingSvc.setTransactionMessage(offerHash, 'Offering punk to stash...');
          const receipt = await this.pollReceipt(offerHash);
          this.txTrackingSvc.setProcessTransactionMessage(receipt, 'Punk offered to stash successfully.');
          
          // Close transaction modal and show final acceptance modal
          setTimeout(() => {
            this.txTrackingSvc.closeTxModal();
            this.isItemViewTransactionActive = false;
            this.currentAcceptStep = 'accept';
            this.acceptCollectionBidFinalModalActive = true;
          }, 2000);
        }
      } else {
        // V2 or V1 wrapped: use standard flow
        // Use the bid amount as the listing price
        const bidAmountEth = parseFloat(this.selectedCollectionBid?.bid_amount_eth || '0');

        if (bidAmountEth <= 0) {
          throw new Error('Invalid bid amount');
        }

        // Use ensureApprovalAndList to handle approval and listing
        const result = await this.web3Svc.ensureApprovalAndList(tokenId, bidAmountEth, stashAddress);
        
        if (result.approvalHash) {
          // Approval is needed
          this.txTrackingSvc.setTransactionMessage(result.approvalHash, 'Approving collection for listing...');
          const approvalReceipt = await this.pollReceipt(result.approvalHash);
          this.txTrackingSvc.setProcessTransactionMessage(approvalReceipt, 'Collection approval confirmed. Now listing...');
          
          // Now proceed with listing
          const listingHash = await this.web3Svc.offerPunkForSale(tokenId, bidAmountEth, stashAddress);
          this.txTrackingSvc.setTransactionMessage(listingHash!, 'Listing punk to stash...');
          
          const listingReceipt = await this.pollReceipt(listingHash!);
          this.txTrackingSvc.setProcessTransactionMessage(listingReceipt, 'Punk listed successfully.');
          
          // Close transaction modal and show final acceptance modal
          setTimeout(() => {
            this.txTrackingSvc.closeTxModal();
            this.isItemViewTransactionActive = false;
            this.currentAcceptStep = 'accept';
            this.acceptCollectionBidFinalModalActive = true;
          }, 2000);
        } else if (result.listingHash) {
          // No approval needed, direct listing
          this.txTrackingSvc.setTransactionMessage(result.listingHash, 'Listing punk to stash...');
          
          const receipt = await this.pollReceipt(result.listingHash);
          this.txTrackingSvc.setProcessTransactionMessage(receipt, 'Punk listed successfully.');
          
          // Close transaction modal and show final acceptance modal
          setTimeout(() => {
            this.txTrackingSvc.closeTxModal();
            this.isItemViewTransactionActive = false;
            this.currentAcceptStep = 'accept';
            this.acceptCollectionBidFinalModalActive = true;
          }, 2000);
        }
      }
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  closeOfferToStashModal(): void {
    this.offerToStashModalActive = false;
    this.stashAddressForListing = null;
    // Reset step tracking
    this.currentAcceptStep = 'initial';
  }

  async approveStashForToken(punk: Punk): Promise<void> {
    try {
      if (!this.stashAddressForListing) {
        throw new Error('No stash address specified');
      }

      const tokenId = punk.id;
      const stashAddress = this.stashAddressForListing;

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      // Approve the stash contract - pass c721wrapped flag for V2
      const c721wrapped = environment.collection === 'cryptopunks' ? punk.c721wrapped : undefined;
      const hash = await this.web3Svc.approveTokenForStash(tokenId, stashAddress, c721wrapped);
      
      if (!hash) {
        throw new Error('Approval transaction failed');
      }

      this.txTrackingSvc.setTransactionMessage(hash, 'Approving stash contract to transfer token...');
      const receipt = await this.pollReceipt(hash);
      this.txTrackingSvc.setProcessTransactionMessage(receipt, 'Stash approval confirmed.');

      // After approval, proceed with accepting the bid
      this.hideSellerApprovalWarning();
      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isItemViewTransactionActive = false;
        this.currentAcceptStep = 'accept';
        this.acceptCollectionBidFinalModalActive = true;
      }, 2000);
      
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  closeAcceptCollectionBidFinalModal(): void {
    this.acceptCollectionBidFinalModalActive = false;
    // Reset step tracking
    this.currentAcceptStep = 'initial';
  }

  async executeAcceptCollectionBidTransaction(punk: Punk): Promise<void> {
    try {
      if (!this.selectedCollectionBid) {
        throw new Error('No collection bid found for this punk');
      }

      const tokenId = punk.id;
      const collectionBid = this.selectedCollectionBid;

      this.closeAcceptCollectionBidFinalModal();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      // Convert expiration timestamp to Unix timestamp if needed
      let expiration: number;
      if (collectionBid.expires_at_utc_timestamp !== undefined && collectionBid.expires_at_utc_timestamp !== null) {
        // Use UTC timestamp directly if provided
        expiration = Number(collectionBid.expires_at_utc_timestamp);
      } else if (collectionBid.expires_at) {
        // Parse as UTC (date is always in UTC format)
        const dateStr = collectionBid.expires_at.toString();
        expiration = Math.floor(new Date(dateStr).getTime() / 1000);
      } else {
        // Default to far future if no expiration provided
        expiration = Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60; // 1 year from now
      }

      // Get signature from the collection bid
      if (!collectionBid.signature) {
        throw new Error('Collection bid signature is missing');
      }

      // Fetch bid details to get the proof for the current punk
      const bidDetails = await firstValueFrom(this.dataSvc.fetchCollectionBidDetails(collectionBid.id));
      if (!bidDetails || bidDetails.error) {
        throw new Error('Failed to fetch collection bid details');
      }

      // Get the proof for the current punk from the proofs object (keyed by punkId)
      const proof = bidDetails.proofs?.[tokenId] || [];
      /*
      if (!proof || proof.length === 0) {
        throw new Error(`Proof not found for punk ${tokenId} in collection bid`);
      }
        */

      const hash = await this.web3Svc.acceptCollectionBidForPunk(
        tokenId,
        collectionBid.bid_amount_wei,
        collectionBid.bidder_address,
        collectionBid.merkle_root,
        collectionBid.account_nonce,
        collectionBid.bid_nonce,
        expiration,
        collectionBid.signature,
        proof
      );
      
      const shortHash = `${hash?.slice(0, 6)}...${hash?.slice(-6)}`;

      this.txTrackingSvc.setTransactionMessage(
        hash!,
        `<p>Your transaction was sent to the MEV Blocker builder and is waiting to be processed.</p>
        <p>Transaction hash: <a href="https://etherscan.io/tx/${hash}" target="_blank">${shortHash}</a></p>
        <p>You can <a href="https://etherscan.io/tx/${hash}" target="_blank">view it here</a> once complete.</p>
      `);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async buyPunk(punk: Punk): Promise<void> {
    try {
      if (this.hasDelegationRights.getValue()) {
        return;
      }
      
      // Check seller approval first
      if (!this.sellerApprovalChecked) {
        await this.checkSellerApproval(punk);
      }
      
      // If seller hasn't approved, show warning
      if (!this.sellerApproved) {
        this.showSellerApprovalWarning();
        return;
      }

      const tokenId = punk.id;
      const value = punk.listing?.value;
      
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.buyPunk(tokenId, value!);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      // const receipt = await this.web3Svc.waitForTransaction(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async submitBid(data: Punk): Promise<void> {
    try {
      if (!this.bidPrice.value) return;
      const tokenId = data.id;
      const value = this.bidPrice.value;

      this.closeBid();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.enterBidForPunk(tokenId, value);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      // const receipt = await this.web3Svc.waitForTransaction(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async submitTransfer(data: Punk): Promise<void> {
    try {
      if (!this.transferAddress.value) return;
      const tokenId = data.id;

      let toAddress: string | null = this.transferAddress.value;
      toAddress = await this.web3Svc.verifyAddressOrEns(toAddress);
      if (!toAddress) throw new Error('Invalid address');

      this.closeTransfer();
      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      let wrappedForTransfer = !!data.wrapped;
      if (environment.collection === 'cryptopunks-v1') {
        wrappedForTransfer = await this.web3Svc.wrappedPunkV1TokenExists(tokenId);
        const punk = this.punk.getValue();
        if (punk && punk.wrapped !== wrappedForTransfer) {
          this.punk.next({ ...punk, wrapped: wrappedForTransfer });
          this.updateWrapUnwrapButtonState();
          this.updateNativeMarketplaceSectionTitle();
        }
      }

      const hash = await this.web3Svc.transferPunk(tokenId, toAddress, wrappedForTransfer);
      if (!hash) {
        throw new Error('Transfer transaction was not submitted');
      }

      this.txTrackingSvc.setTransactionMessage(hash);

      const receipt = await this.pollReceipt(hash);
      if (receipt.status === 'reverted') {
        throw new Error('Transfer transaction reverted on-chain');
      }

      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      this.closeAll();
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async startAuction(data: Punk): Promise<void> {
    try {
      this.isItemViewTransactionActive = true;
      const ethPrice = environment.auctionStarterValue + '.' + String(data.id).padStart(4, '0');
      const weiPrice = this.web3Svc.ethToWei(Number(ethPrice));

      const hash = await this.web3Svc.sendCig(
        environment.auctionStarterAddress,
        weiPrice.toString()
      );
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);

      await firstValueFrom(this.getSetAuction(data.id));
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);

    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  async addMevBlockerNetwork(): Promise<void> {
    try {
      return await this.web3Svc.addMevBlockerNetwork();
    } catch (err) {
      console.log(err);
    }
  }

  private registerV1ListingUiOrigin(
    tokenId: string,
    listingHash: string,
    listingReceipt: TransactionReceipt,
    amountEth: number,
    submittedAtBlock: number,
    action: 'v1-listing' | 'v1-punksmarket-listing' = 'v1-listing',
  ): void {
    if (environment.collection !== 'cryptopunks-v1') {
      return;
    }

    const walletAddress = this.stateSvc.getWalletAddress();
    if (!walletAddress || !listingHash) {
      return;
    }

    this.dataSvc.registerUiTransactionOrigin({
      txHash: listingHash,
      walletAddress,
      tokenId,
      action,
      displaySource: 'v1cryptopunks.com',
      submittedAtBlock,
      confirmedBlock: Number(listingReceipt.blockNumber),
      chainId: environment.chainId,
      amountEth,
    }).subscribe();
  }

  async pollReceipt(hash: string): Promise<TransactionReceipt> {
    let resolved = false;
    return new Promise(async (resolve, reject) => {
      while (!resolved) {
        console.log('polling receipt', hash);
        try {
          const receipt = await this.web3Svc.waitForTransaction(hash);
          if (receipt) {
            resolved = true;
            resolve(receipt);
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  }

  async claimCig(): Promise<void> {
    try {
      const tokenId = this.punk.getValue()?.id;
      if (!tokenId) return;

      this.isItemViewTransactionActive = true;
      this.txTrackingSvc.initTransactionMessage();

      const hash = await this.web3Svc.claimCig(tokenId);
      this.txTrackingSvc.setTransactionMessage(hash!);

      const receipt = await this.pollReceipt(hash!);
      this.txTrackingSvc.setProcessTransactionMessage(receipt);
      await this.web3Svc.getCigBalance();
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    } catch (err) {
      console.log(err);
      this.txTrackingSvc.setErrorTransactionMessage(err);
      setTimeout(() => {
        this.isItemViewTransactionActive = false;
      }, 3000);
    }
  }

  /**
   * Check if the seller has approved the marketplace for this token
   */
  async checkSellerApproval(punk: Punk): Promise<void> {
    if (!punk.owner?.id || !punk.listing) return;
    
    try {
      this.sellerApproved = await this.web3Svc.checkSellerApproval(punk.id, punk.owner.id);
      this.sellerApprovalChecked = true;
    } catch (error) {
      console.error('Error checking seller approval:', error);
      this.sellerApproved = false;
      this.sellerApprovalChecked = true;
    }
  }

  /**
   * Check if the current user has delegation rights for this specific punk token
   */
  private async checkDelegationRights(userAddress: string, punkOwner: string): Promise<void> {
    try {
      const punk = this.punk.getValue();
      if (!punk) {
        console.warn('No punk data available for delegation check');
        this.hasDelegationRights.next(false);
        return;
      }

      // Check delegation rights specifically for this token
      const hasRights = await this.delegateXYZService.hasDelegationRightsForToken(
        punkOwner,                    // delegator (current owner)
        userAddress,                  // delegate (user who wants to act)
        environment.wrappedPunksAddress, // contract address
        punk.id                       // specific token ID
      );

      console.log(`Delegation rights for punk ${punk.id}:`, hasRights);
      this.hasDelegationRights.next(hasRights);
    } catch (error) {
      console.error('Error checking delegation rights for token:', error);
      this.hasDelegationRights.next(false);
    }
  }

  /**
   * Show seller approval warning modal
   */
  showSellerApprovalWarning(): void {
    this.sellerApprovalWarningModalActive = true;
  }

  /**
   * Hide seller approval warning modal
   */
  hideSellerApprovalWarning(): void {
    this.sellerApprovalWarningModalActive = false;
  }

  getItemQueryParams(item: any): any {
    return { [item.k.replace(/ /g, '-').toLowerCase()]: item.v.replace(/ /g, '-').toLowerCase() };
  }

  getFilteredMetadata(metadatas: any) {
    return [
      { label: 'discord', value: metadatas.discord },
      { label: 'x', value: metadatas.x },
      { label: 'telegram', value: metadatas.telegram },
      { label: 'github', value: metadatas.github },
      { label: 'url', value: metadatas.url },
    ].filter(item => !!item.value);
  }
  
  protected readonly environment = environment;

  /**
   * Update wrap/unwrap button state based on current punk and user
   */
  private updateWrapUnwrapButtonState(): void {
    const punk = this.punk.getValue();

    let shouldShow = false;
    if (punk) {
      if (environment.collection === 'cryptopunks' || environment.collection === 'cryptopunks-v1') {
        const currentUserAddress = this.stateSvc.getWalletAddress();
        if (currentUserAddress && punk.owner?.id) {
          const isOwner = currentUserAddress.toLowerCase() === punk.owner.id.toLowerCase();
          shouldShow = isOwner;
        }
      }
    }

    this.canShowWrapUnwrapButton = shouldShow;

    let buttonText = '';
    if (punk && shouldShow) {
      if (punk.wrapped || punk.c721wrapped) {
        buttonText = 'Unwrap';
      } else {
        buttonText = 'Wrap';
      }
    }

    this.wrapUnwrapButtonText = buttonText;
  }

  private handleOwnedWrapStatusChanged = (domEvent: globalThis.Event): void => {
    if (!(domEvent instanceof CustomEvent)) {
      return;
    }

    const detail = domEvent.detail as {
      wallet?: string;
      updates?: Array<{ punkId: string; wrapped: boolean }>;
    };
    const wallet = detail?.wallet?.toLowerCase();
    const updates = detail?.updates;
    const punk = this.punk.getValue();

    if (!wallet || !punk || !Array.isArray(updates) || updates.length === 0) {
      return;
    }

    const currentUserAddress = this.stateSvc.getWalletAddress()?.toLowerCase();
    if (!currentUserAddress || wallet !== currentUserAddress) {
      return;
    }

    const update = updates.find((entry) => String(entry.punkId) === String(punk.id));
    if (update === undefined) {
      return;
    }

    this.punk.next({ ...punk, wrapped: update.wrapped });
    this.updateWrapUnwrapButtonState();
    this.updateNativeMarketplaceSectionTitle();
  };

  private updateNativeMarketplaceSectionTitle(): void {
    const punk = this.punk.getValue();

    if (
      !punk ||
      (environment.collection !== 'cryptopunks' && environment.collection !== 'cryptopunks-v1')
    ) {
      this.nativeMarketplaceSectionTitle = 'Native marketplace';
      return;
    }

    this.nativeMarketplaceSectionTitle = isOwnedPunkWrapped(punk)
      ? 'Native wrapped marketplace'
      : 'Native unwrapped marketplace';
  }

  /**
   * Open the side menu and select the current punk for wrap/unwrap operations
   */
  async openWrapUnwrapMenu(): Promise<void> {
    const punk = this.punk.getValue();
    if (!punk) {
      console.warn('No punk data available for wrap/unwrap operation');
      return;
    }

    const currentUserAddress = this.stateSvc.getWalletAddress();
    if (!currentUserAddress) {
      console.warn('No wallet connected for wrap/unwrap operation');
      return;
    }

    try {
      // Set the active tab to wrapping-tools (second tab)
      this.stateSvc.setSideMenuActiveTab('wrapping-tools');
      
      // Open the side menu
      this.stateSvc.setMenuOpen(true);

      // Allow the sidebar and wrapper component to mount before selecting the punk
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Select the current punk in the wrapper component
      await this.selectPunkInWrapper(Number(punk.id), !!(punk.wrapped || punk.c721wrapped));
      
    } catch (error) {
      console.error('Error opening wrap/unwrap menu:', error);
    }
  }

  /**
   * Wait for the wrapper component to be ready
   */
  private async waitForWrapperComponent(): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20; // Maximum 10 seconds (20 * 500ms)
      
      const checkWrapper = () => {
        attempts++;
        
        // Check if the wrapper component is ready by looking for the wrapper element
        const wrapperElement = document.querySelector('app-v2-wrapper');
        
        if (wrapperElement && attempts > 2) {
          // Give a bit more time for the component to fully initialize
          setTimeout(() => resolve(), 200);
        } else if (attempts >= maxAttempts) {
          console.warn('Wrapper component did not load within expected time');
          resolve(); // Resolve anyway to avoid hanging
        } else {
          setTimeout(checkWrapper, 500);
        }
      };
      
      checkWrapper();
    });
  }

  /**
   * Select the punk in the wrapper component
   * This method will be called by the wrapper component when it's ready
   */
  private async selectPunkInWrapper(punkId: number, isWrapped: boolean): Promise<void> {
    // We'll need to communicate with the wrapper component
    // For now, we'll use a custom event or service to pass the selection
    const event = new CustomEvent('selectPunkForWrap', {
      detail: {
        punkId: punkId,
        isWrapped: isWrapped
      }
    });
    
    window.dispatchEvent(event);
  }

  private checkSiblingPairing(punk: Punk): void {
    this.siblingPaired = false;
    this.siblingOwner = null;
    if (!environment.crossMarketplaceUrl || !environment.featureFlags?.CROSS_MARKETPLACE_COUNTERPART) return;

    const siblingCollection =
      environment.collection === 'cryptopunks-v1'
        ? environment.v2PunksMarketAddress
        : environment.collection === 'cryptopunks'
          ? environment.v1PunksContractAddress
          : null;

    if (!siblingCollection) return;

    const currentOwner = (punk.wrappedOwner || punk.owner?.id)?.toLowerCase();

    this.dataSvc.fetchTokenOwner(punk.id, siblingCollection).pipe(take(1)).subscribe((owner) => {
      this.siblingOwner = owner;
      this.siblingPaired = !!owner && !!currentOwner && owner === currentOwner;
    });
  }

  getThirdPartyBidsTooltipContent(): string {
    if (!this.thirdPartyBids || !Array.isArray(this.thirdPartyBids)) {
      return '';
    }

    // Sort offers by decreasing amount
    const sortedBids = [...this.thirdPartyBids].sort((a, b) => 
      Number(b.price.amount.decimal) - Number(a.price.amount.decimal)
    );

    // Take the first 3 offers
    const topBids = sortedBids.slice(0, 3);

    const result = topBids
      .map(bid => {
        const ethValue = Number(bid.price.amount.decimal);
        const usdValue = ethValue * this.usdRate.value;
        return `${bid?.source?.name ?? 'Unknown'} for ${ethValue.toFixed(2)} ETH ($${usdValue.toFixed(2)})`;
      })
      .join('<br>');

    // Ajouter le nombre d'offres restantes s'il y en a plus de 3
    if (sortedBids.length > 3) {
      const remainingCount = sortedBids.length - 3;
      return result + `<br>... and ${remainingCount} more ${remainingCount > 1 ? 'bids' : 'bid'}`;
    }

    return result;
  }


}
